import { useNavigate } from "react-router-dom";
import PageContainer from "../components/layout/PageContainer";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { useApi } from "../hooks/useApi";
import { endpointsApi } from "../api/endpoints";

export default function Endpoints() {
  const navigate = useNavigate();
  const endpoints = useApi(() => endpointsApi.list(), []);

  if (endpoints.loading) {
    return <LoadingSpinner message="Loading endpoints..." />;
  }

  if (endpoints.error) {
    return (
      <PageContainer title="Endpoints" description="SIM and eSIM management">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-700">
            Unable to load endpoints: {endpoints.error}
          </p>
        </div>
      </PageContainer>
    );
  }

  const endpointList = endpoints.data?.data ?? [];

  return (
    <PageContainer
      title="Endpoints"
      description={`${endpointList.length} SIM${endpointList.length !== 1 ? "s" : ""} configured`}
    >
      {endpointList.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <p className="text-sm text-gray-500">No endpoints configured</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {endpointList.map((ep) => (
            <button
              key={ep.id}
              onClick={() => navigate(`/endpoints/${ep.id}`)}
              className="rounded-lg border border-gray-200 bg-white p-4 text-left transition-shadow hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {ep.name || "Unnamed"}
                  </p>
                  <p className="mt-1 truncate text-xs text-gray-500">
                    {ep.iccid}
                  </p>
                  {ep.msisdn && (
                    <p className="mt-0.5 text-xs text-gray-500">
                      {ep.msisdn}
                    </p>
                  )}
                </div>
                <StatusBadge status={ep.status} />
              </div>
              {ep.tags && ep.tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {ep.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              {ep.groupName && (
                <p className="mt-2 text-xs text-gray-400">{ep.groupName}</p>
              )}
            </button>
          ))}
        </div>
      )}
    </PageContainer>
  );
}

function StatusBadge({ status }: { status: string }) {
  const isActive =
    status === "ENABLED" || status === "ONLINE" || status === "ACTIVE";
  return (
    <span
      className={`ml-2 inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        isActive
          ? "bg-green-100 text-green-700"
          : "bg-gray-100 text-gray-600"
      }`}
    >
      <span
        className={`mr-1 h-1.5 w-1.5 rounded-full ${
          isActive ? "bg-green-500" : "bg-gray-400"
        }`}
      />
      {status}
    </span>
  );
}
